//src/app/features/taxExtensions/index.tsx
import { useGetClientProfile } from 'admin/features/clients/viewClientDetails/accountInfoClientTab';
import {
  useDeleteTaxExtensionMutation,
  useLazyGetTaxExtensionsQuery,
  usePayTaxExtensionMutation,
} from 'app/services/taxExtensions';
import { AddIcon, EditIcon, TrashCanSolidIcon } from 'assets/icons';
import Button from 'Components/Buttons/Button';
import { USDate } from 'Components/Dates/USDate';
import { Filters, FilterSection, RadioGroup } from 'Components/Filters';
import { Grid } from 'Components/Grid';
import usePagination from 'Components/Pagination/usePagination';
import { useConfirmDeleteModal } from 'Components/Popups/ConfirmDeletePopup';
import { ClientBusinessTaskTaxReturnSelect } from 'Components/Selectors/ClientBusinessTaskTaxReturnSelect';
import { Link, useNavigate } from 'react-router-dom';

export function TaxExtensions() {
  const { client, isClient, clientId } = useGetClientProfile();
  const { data, Pagination, filters, setFilters, refetchAfterDelete } =
    usePagination(useLazyGetTaxExtensionsQuery, {
      filters: {
        client_id: client?.id,
      },
      per_page: 10,
    });

  const [DeleteTaxExtension] = useDeleteTaxExtensionMutation();
  const [payTaxExtension] = usePayTaxExtensionMutation(); // Mutation for payment
  const navigate = useNavigate();
  const confirmDelete = useConfirmDeleteModal();

  const handlePayment = async (requestId: string) => {
    try {
      const response = await payTaxExtension(requestId).unwrap();
      if (response.checkout_url) {
        window.location.href = response.checkout_url; // Redirect to payment URL
      } else {
        console.error('Payment URL not returned.');
      }
    } catch (error) {
      console.error('Payment initiation failed', error);
    }
  };

  return (
    <>
      {isClient && (
        <div className="mb-4 md:hidden">
          <Link to="create">
            <Button type="button">
              <AddIcon />
              Add new
            </Button>
          </Link>
        </div>
      )}
      <Grid
        footer={<Pagination />}
        headerClassName="border-b border-gray-100 text-gray-dark bg-white"
        isLoading={!data}
        columns={[
          { render: 'Tax Extensions', width: '2fr' },
          'Type',
          'Year',
          'Status',
          'Payment', // Add this line for payment status
          'Date',
          'Price',
          {
            render: (
              <div className="flex gap-2">
                <Filters
                  isLoading={!data}
                  value={filters}
                  onConfirm={setFilters}
                  preExpanded={['status']}
                  locked={client ? ['client_id'] : undefined}
                >
                  {({ updateField, state }) => (
                    <>
                      <FilterSection>
                        <div className="w-full">
                          <ClientBusinessTaskTaxReturnSelect
                            clientId={client ? client?.id : state?.client_id}
                            businessId={state.business_id}
                            lockClient={client}
                            withProjectSelector={false}
                            withTaskSelector={false}
                            onChange={({ clientId, businessId }) => {
                              updateField('client_id', clientId);
                              updateField('business_id', businessId);
                            }}
                          />
                        </div>
                      </FilterSection>

                      <RadioGroup
                        label="Status"
                        name="status"
                        checked={state.status}
                        options={[
                          { value: 'submitted', label: 'Submitted' },
                          { value: 'in_progress', label: 'In Progress' },
                          { value: 'completed', label: 'Completed' },
                          { value: 'cancelled', label: 'Cancelled' },
                        ]}
                        onUpdateField={updateField}
                      />
                    </>
                  )}
                </Filters>

                {/* Desktop / tablet button */}
                {isClient && (
                  <Link to="create" className="hidden md:inline-flex">
                    <Button type="button">
                      <AddIcon />
                      Add new
                    </Button>
                  </Link>
                )}
              </div>
            ),
            width: isClient ? '150px' : '100px',
          },
        ]}
        data={data}
        onRowClick={({ data }) => navigate(data.id.toString())}
        renderCells={(tex) => [
          tex.title,
          tex.type,
          tex.year,
          tex.status.replace('_', ' '),

          // Conditional rendering of payment status
          isClient ? (
            tex.payment_status === 'paid' ? (
              <span className="text-green-500 font-semibold">Paid</span>
            ) : (
              <Button
                onClick={() => handlePayment(String(tex.id))}
                className="bg-transparent text-red-500 font-semibold py-1 px-2 border border-green-500 rounded hover:bg-green-50 hover:text-green-500 transition duration-300 text-center"
                style={{
                  color: 'red',
                  whiteSpace: 'normal', // Allow text to wrap
                  width: '45px', // Set a fixed width for compactness
                  lineHeight: '1.2', // Adjust line height to reduce spacing
                  padding: '4px 8px', // Compact padding
                }}
              >
                Pay?
              </Button>
            )
          ) : tex.payment_status === 'paid' ? (
            <span className="text-green-500 font-semibold">Paid</span>
          ) : (
            <span className="text-red-500 font-semibold">Unpaid</span>
          ), // Render "Pay Now?" button for client, "Unpaid" for accountant

          <USDate date={tex.created_at} />,
          tex.extension_price && (
            <span className="text-green font-semibold">
              ${tex.extension_price}
            </span>
          ),
          isClient && tex.status === 'submitted' && (
            <div
              className="flex gap-4 items-center max-w-min"
              onClick={(e) => e.stopPropagation()}
            >
              <Button onClick={() => navigate(`${tex.id}/edit`)}>
                <EditIcon className="w-4 h-4" />
              </Button>
              <Button
                theme="red-outline"
                onClick={() =>
                  confirmDelete('Tax Extension', () =>
                    DeleteTaxExtension(tex.id)
                      .unwrap()
                      .then(refetchAfterDelete),
                  )
                }
              >
                <TrashCanSolidIcon className="w-4 h-4 text-red-200" />
              </Button>
            </div>
          ),
        ]}
      />
    </>
  );
}
